import type {
  CellData,
  ColumnDef,
  ColumnMeta,
  RowData,
  TableFeatures,
} from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { cacheByItem } from '../../helpers/cache-by-item';
import { compare, type SortKey } from '../../helpers/column-sort';
import type { AppRow, AppTableFeatures } from './features';

/**
 * Extend TanStack Table's ColumnMeta to include application-specific fields.
 * This allows us to attach custom metadata to columns for CSV export, styling, etc.
 */
declare module '@tanstack/react-table' {
  interface ColumnMeta<
    in out TFeatures extends TableFeatures,
    in out TData extends RowData,
    TValue extends CellData = CellData,
  > {
    /** Minimum column width in pixels */
    minWidth: number;
    /** Maximum column width in pixels (columns with maxWidth can expand) */
    maxWidth?: number;

    /** Convert value to string representation (for CSV fallback, print, etc.) */
    toString: (value: TData) => string;
    /** Custom CSV export formatter - returns key-value pairs for multiple CSV columns */
    toCSV?: (item: TData) => { [key: string]: string };

    /** Plain-language column description, surfaced via an info icon in the header meta-row. */
    gloss?: ReactNode;

    /**
     * This row's place in the column's ordering, as a value that can be
     * compared directly. Present only when the ordering is a pure function of
     * one derived value per row — which is every column except the ones passing
     * their own `sortingFn`.
     *
     * createSortedRowModel reads this to compute each row's key once per sort
     * rather than inside the comparator. A column without it still sorts, via
     * the `sortFn` comparator; it just doesn't get decorated.
     */
    sortKey?: (value: TData) => SortKey;
  }
}

/** Minimal row type for cell rendering — keeps remapColumn's cells constructable */
export type CellRow<TData extends RowData> = Pick<AppRow<TData>, 'original' | 'id'>;

/** Minimal table surface needed by cell renderers (visual index lookup) */
export type CellTable = {
  getSortedRowModel: () => { flatRows: ReadonlyArray<{ id: string }> };
};

/** Minimal row type for sorting (subset of TanStack Row) */
type SortRow<TData extends RowData> = Pick<AppRow<TData>, 'original'>;

export type StrictColumnDef<TData extends RowData> = Omit<
  ColumnDef<AppTableFeatures, TData, unknown>,
  'accessorFn' | 'cell' | 'sortFn' | 'meta' | 'header'
> & {
  id: string;
  header: string | (() => ReactNode);
  accessorFn: (value: TData) => string;
  /**
   * Custom cell renderer. Compatible with TanStack CellContext — getValue is
   * typed unknown because v9 erases TValue to unknown in the columns option;
   * defineColumn derives the string value from accessorFn instead.
   */
  cell?: (props: { table: CellTable; row: CellRow<TData>; getValue: () => unknown }) => ReactNode;
  sortFn?: (rowA: SortRow<TData>, rowB: SortRow<TData>, columnId: string) => number;
  meta: ColumnMeta<AppTableFeatures, TData, unknown>;
};

/**
 * Configuration for defining a TanStack column with application-specific features.
 * Provides a clean API similar to the legacy ColumnDefinition while returning
 * a native TanStack ColumnDef.
 */
export type ColumnConfig<T extends RowData> = {
  /** Column identifier, and the header label unless `header` says otherwise */
  id: string;
  /**
   * Header label, when it should differ from the id. A column whose label
   * follows a filter — "90d Profit" — keeps a stable id this way, so a sort or
   * saved layout on it survives the filter changing.
   */
  header?: string;
  /** Minimum column width in pixels */
  minWidth: number;
  /** Maximum column width in pixels (columns with maxWidth can expand) */
  maxWidth?: number;

  /** Convert value to string representation (for CSV fallback, print, etc.) */
  accessorFn: (value: T) => string;

  /**
   * This row's place in the ordering, when it is a pure function of the row.
   *
   * Prefer this to `sortingFn`. A key is computed once per row per sort and
   * then only indexed, where a comparator runs twice per comparison — ~178,000
   * of them on the 13,000-row order-edit table, on every keystroke. Ordering is
   * `compare`'s: numbers and dates by value, strings by locale, nullish last.
   *
   * Without it the key is `accessorFn`, the rendered string.
   */
  sortKeyFn?: (item: T) => SortKey;

  /**
   * Keep rows with a nullish `sortKeyFn` key last in both directions, instead
   * of last ascending and first descending. For a column where "no value" is
   * the least interesting answer either way — a vendor never received from
   * should not top a newest-first sort. Becomes `sortUndefined: 'last'`.
   */
  nullsLast?: boolean;

  /**
   * Custom comparison function for sorting.
   *
   * Only for an ordering that genuinely depends on both rows and so cannot
   * collapse to one value per row — Cases Sold, which switches which field it
   * compares when either row's `case_qty` is 0. Such a column publishes no
   * `meta.sortKey` and sorts through this comparator instead. Everything else
   * wants `sortKeyFn`.
   */
  sortingFn?: (rowA: T, rowB: T) => number;

  /** Custom cell renderer. Destructure only what you need. */
  cell?: (props: { table: CellTable; row: CellRow<T>; item: T; value: string }) => ReactNode;

  /** Custom CSV export formatter - returns key-value pairs for multiple CSV columns */
  toCSV?: (row: T) => { [key: string]: string };

  /**
   * Plain-language column description. Rendered as an info icon in the header
   * cell's meta-row (see table.tsx) — never inside the label's box, so it can't
   * collide with a wrapping multi-line label.
   */
  gloss?: ReactNode;
};

/**
 * Create a TanStack ColumnDef with application-specific configuration.
 *
 * @example
 * ```tsx
 * const itemCodeColumn = defineColumn<Product>({
 *   id: 'Item Code',
 *   minWidth: 55,
 *   getValue: (product) => product.item_code || product.suggested_item_code,
 *   cell: ({ value }) => <span>{value}</span>,
 * });
 * ```
 */
export function defineColumn<T extends RowData>(config: ColumnConfig<T>): StrictColumnDef<T> {
  const { id, header = id, minWidth, maxWidth, accessorFn, cell, toCSV, gloss, nullsLast } = config;

  const { sortKey, compareRows } = resolveSort(config);

  return {
    id,
    header,
    accessorFn,

    cell: cell
      ? ({ row, table }) => {
          return cell({ item: row.original, row, value: accessorFn(row.original), table });
        }
      : ({ row }) => accessorFn(row.original),

    sortFn: (rowA, rowB, _columnId) => compareRows(rowA.original, rowB.original),
    enableSorting: true,

    // rowSortingFeature defaults every column to `sortUndefined: 1`, which makes
    // the library's compareRows call row.getValue() on BOTH rows of every
    // comparison purely to check for undefined. accessorFn returns a string, so
    // that probe can never change an outcome — it was a full second of the
    // ~13.7s spent typing into a 13k-row table. Opt out. `nullsLast` reuses
    // 'last', which the decorated row model applies to the key instead.
    sortUndefined: nullsLast ? 'last' : false,

    // Size configuration
    minSize: minWidth,
    maxSize: maxWidth,
    size: minWidth,

    // Store properties in meta for CSV export, print, sizing, etc.
    meta: {
      toString: accessorFn,
      toCSV,
      gloss,
      minWidth,
      maxWidth,
      sortKey,
    },
  };
}

/**
 * Resolve a column's ordering into the two forms the table needs: the key the
 * row model decorates with, and the comparator `sortFn` falls back on.
 *
 * Exactly one of the three shapes applies, and they are checked in the order a
 * caller means them:
 *
 * - `sortingFn` is an opaque comparator, so there is no single value per row to
 *   precompute. Those columns publish no key and sort through `sortFn` alone.
 * - `sortKeyFn` is the key the caller wrote.
 * - Neither, so the rendered string is the key — which is what it has always
 *   been for a column that configures no sorting at all.
 *
 * Both key shapes go through the same cache. The accessors here are derived
 * values — reorderLabel, formatUnitsQuantity, four-level reaches into
 * `oi.product` — so deriving one key per row beats recomputing inside the
 * comparator, and !4431 measured the cache pulling its own weight on top of the
 * decoration (243.6 ms median with both, 283.8 ms with decoration alone).
 * The cache belongs to this `defineColumn` call, so a column whose key reads
 * settings (useReorder) gets a fresh one as soon as those settings build a new
 * column — which is only sound while such a column is rebuilt inside a memo
 * keyed on those settings.
 *
 * Keeping `sortFn` in step with the key matters even though the row model reads
 * the key: a column is still sortable through the library's own path (the
 * comparator is what `remapColumn` composes, and what a row with no projection
 * falls back to), and the two disagreeing would order the same column two ways.
 */
function resolveSort<T extends RowData>(
  config: ColumnConfig<T>
): { sortKey?: (item: T) => SortKey; compareRows: (rowA: T, rowB: T) => number } {
  const { accessorFn, sortKeyFn, sortingFn } = config;

  if (sortingFn) {
    return { compareRows: sortingFn };
  }

  const sortKey = cacheByItem<T, SortKey>(sortKeyFn ?? accessorFn);

  return { sortKey, compareRows: (rowA, rowB) => compare(sortKey(rowA), sortKey(rowB)) };
}

/**
 * Adapt a column written for one row type into a column over another, by
 * projecting each row to the row the column already understands.
 *
 * Only the six fields that read a row need composing — `accessorFn`, `cell`,
 * `sortFn`, `meta.toString`, `meta.toCSV`, `meta.sortKey`. Everything else is
 * carried through untouched, so an option added to `defineColumn` reaches every
 * adapted column with no edit here and none at any call site. That is the whole
 * point: the two hand-written adapters this replaced each maintained their own
 * field list, and one of them silently dropped `sortUndefined` when it was
 * introduced.
 *
 * `project` may return undefined for a row the column has nothing to say about
 * — an exception log whose product isn't in the catalog. Such a row reads as
 * empty everywhere: an empty accessor string, no cell, and no CSV columns of
 * its own (papaparse fills the gap from the union of keys).
 */
export function remapColumn<TFrom extends RowData, TTo extends RowData>(
  column: StrictColumnDef<TFrom>,
  project: (row: TTo) => TFrom | undefined
): StrictColumnDef<TTo> {
  const { accessorFn, cell, sortFn, meta, ...rest } = column;

  const accessor = (row: TTo) => {
    const from = project(row);

    return from === undefined ? '' : accessorFn(from);
  };

  // Captured so TypeScript keeps them narrowed inside the closures below.
  const { toString: sourceToString, toCSV: sourceToCSV, sortKey: sourceSortKey } = meta;

  return {
    // `rest` holds the fields ColumnDef parameterises by row type but that
    // nothing in this codebase ever sets — footer, aggregationFn and friends.
    // Re-typing them for TTo is the single unsound step here, and it is sound
    // exactly while that stays true. Everything a column does set is either
    // row-type-independent (id, header, sizes, enableSorting, sortUndefined)
    // or composed with `project` below.
    ...(rest as Omit<StrictColumnDef<TTo>, 'accessorFn' | 'cell' | 'sortFn' | 'meta'>),

    accessorFn: accessor,

    cell:
      cell &&
      (({ row, getValue, table }) => {
        const from = project(row.original);

        return from === undefined
          ? null
          : cell({ row: { original: from, id: row.id }, getValue, table });
      }),

    sortFn:
      sortFn &&
      ((rowA, rowB, columnId) => {
        const a = project(rowA.original);
        const b = project(rowB.original);

        if (a === undefined || b === undefined) {
          // A row with no projection has nothing for the comparator to read.
          // Fall back to the accessor, which is '' for exactly those rows, so
          // they group together at one end instead of ordering arbitrarily
          // against each other.
          const keyA = a === undefined ? '' : accessorFn(a);
          const keyB = b === undefined ? '' : accessorFn(b);

          return keyA.localeCompare(keyB);
        }

        return sortFn({ original: a }, { original: b }, columnId);
      }),

    meta: {
      ...meta,
      toString: (row: TTo) => {
        const from = project(row);

        return from === undefined ? '' : sourceToString(from);
      },
      toCSV:
        sourceToCSV &&
        ((row: TTo) => {
          const from = project(row);

          return from === undefined ? {} : sourceToCSV(from);
        }),
      // A row with no projection keys as `''`, the same empty string its
      // accessor and its comparator fallback already use, so all three place it
      // identically. Deliberately not `null`, which `compare` sends to the far
      // end: `''` orders ahead of any real string and, against numbers and
      // dates, coerces to 0 — which is where such a row sorts today. The point
      // here is to preserve a placement, not to pick a better one.
      sortKey:
        sourceSortKey &&
        ((row: TTo) => {
          const from = project(row);

          return from === undefined ? '' : sourceSortKey(from);
        }),
    },
  };
}

/** Type guard to narrow ColumnDef to StrictColumnDef (for extracting columns from TanStack Table API) */
export function isStrictColumnDef<T extends RowData>(
  col: ColumnDef<AppTableFeatures, T, unknown>
): col is ColumnDef<AppTableFeatures, T, unknown> & StrictColumnDef<T> {
  return typeof col.id === 'string' && col.meta !== undefined;
}
